import React, { useState, useEffect } from 'react';
import {
  Cloud,
  FileText,
  Image as ImageIcon,
  File,
  Download,
  Eye,
  ArrowLeft,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import sharingService from '../services/sharingService';
import PdfViewer from '../components/PdfViewer';
import { formatFileSize } from '../utils/helpers';
import { cn } from '../lib/utils';

// Shadcn UI Components
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert"

const SharedFile = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid share link - no token provided');
      setLoading(false);
      return;
    }

    loadSharedFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSharedFile = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await sharingService.getSharedFileInfo(token);
      setFile(response.file);

      // Auto-preview for images
      if (response.file.mimeType?.startsWith('image/')) {
        const imageUrl = sharingService.getSharedFileUrl(token);
        setImageUrl(imageUrl);
      }

    } catch (err) {
      console.error('Error loading shared file:', err);
      const errorMessage = err.response?.data?.message ||
        err.message ||
        'File tidak ditemukan atau link sudah dicabut';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!token) return;

    setDownloading(true);

    // Create direct download URL
    const downloadUrl = sharingService.getSharedFileUrl(token);

    // Method 1: Open in new window (will trigger download)
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = file?.name || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setDownloading(false);
    }, 1000);
  };

  const handleView = () => {
    if (!file) return;

    if (file.mimeType === 'application/pdf') {
      setPdfViewerOpen(true);
    } else if (file.mimeType?.startsWith('image/')) {
      const imageUrl = sharingService.getSharedFileUrl(token);
      setImageUrl(imageUrl);
    } else {
      handleDownload();
    }
  };

  const getFileIcon = () => {
    if (!file?.mimeType) return <File className="h-16 w-16 text-gray-400" />;

    if (file.mimeType === 'application/pdf') {
      return <FileText className="h-16 w-16 text-red-500" />;
    }
    if (file.mimeType.startsWith('image/')) {
      return <ImageIcon className="h-16 w-16 text-green-500" />;
    }
    return <File className="h-16 w-16 text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">Memuat file...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <CardTitle>Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">{error}</p>
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Kembali ke Utama
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const isPdf = file?.mimeType === 'application/pdf';
  const isImage = file?.mimeType?.startsWith('image/');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-10">
      <div className="container max-w-3xl mx-auto px-4">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="p-3 bg-primary/10 rounded-xl text-primary">
            <Cloud className="h-10 w-10" />
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-2xl font-bold tracking-tight">Data Storage Sharing</h1>
            <p className="text-muted-foreground">PDTI-UMI File Sharing</p>
          </div>
        </div>

        <Card className="overflow-hidden shadow-lg border-t-4 border-t-primary">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              <div className="bg-secondary/30 p-6 rounded-2xl border border-secondary">
                {getFileIcon()}
              </div>

              <div className="flex-1 text-center md:text-left space-y-4">
                <div>
                  <h2 className="text-2xl font-bold break-words">{file.name}</h2>
                  <div className="flex items-center gap-2 mt-2 justify-center md:justify-start">
                    <Badge variant="secondary" className="text-sm font-normal">
                      {formatFileSize(file.size)}
                    </Badge>
                    <Badge variant="outline" className="text-sm font-normal text-muted-foreground uppercase">
                      {file.mimeType.split('/')[1] || 'FILE'}
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 justify-center md:justify-start pt-2">
                  {(isPdf || isImage) && (
                    <Button onClick={handleView} size="lg" className="w-full md:w-auto">
                      <Eye className="h-5 w-5 mr-2" />
                      Preview
                    </Button>
                  )}
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    size="lg"
                    variant={isPdf || isImage ? "outline" : "default"}
                    className="w-full md:w-auto"
                  >
                    {downloading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Mengunduh...
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Download
                      </>
                    )}
                  </Button>
                </div>

                <Alert className="bg-blue-50 text-blue-800 border-blue-200 mt-4">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">Public Link</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    File ini dibagikan secara publik. Login tidak diperlukan.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </CardContent>
        </Card>

        {isImage && imageUrl && (
          <Card className="mt-6 overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">Image Preview</CardTitle>
            </CardHeader>
            <CardContent className="bg-secondary/20 flex justify-center p-4">
              <img
                src={imageUrl}
                alt={file.name}
                className="max-w-full max-h-[70vh] rounded shadow-sm object-contain"
              />
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-8">
          <Button variant="ghost" onClick={() => navigate('/')}>
            Kembali ke Utama
          </Button>
        </div>

      </div>

      {/* PDF Viewer */}
      {isPdf && (
        <PdfViewer
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          file={{
            name: file.name,
            downloadUrl: sharingService.getSharedFileUrl(token, file.name)
          }}
        />
      )}
    </div>
  );
};

export default SharedFile;